import { getModule, Module, Mutation, VuexModule } from 'vuex-module-decorators';
import store from '@/store';
import router from '@/router';
import Paths from '@/constants/Paths';
import { CustomerProfileVm } from '@/api';
import { jwtDecode } from 'jwt-decode';
import UserAccount from '@/store/modules/userAccount';
import CardApplication from '@/store/modules/cardApplication';
import CustomerModule from '@/store/modules/customer';
import ShoppingCartModule from '@/store/modules/shoppingCart';
import SystemConfigurationModule from '@/store/modules/systemConfiguration';
import { formatName } from '@/utils/customer';
import { differenceInMilliseconds } from 'date-fns';
import { loadPublicSystemConfiguration } from '@/utils/systemConfiguration';

interface JwtInterface {
  customerAccountId: string;
  iat: number;
  exp: number;
}

@Module({
  dynamic: true,
  store,
  namespaced: true,
  name: 'auth',
  preserveState: localStorage.getItem('vuex') !== null,
})
class Auth extends VuexModule {
  public logged = false;
  // User has JWT but doesn't have access to application or account
  // usefull for oauth login flows
  public partiallyLogged = false;
  public token = '';
  public customerAccountId = '';
  public username = '';
  public tokenExpiresIn: string | Date = '';
  private logoutWhenExpiredTimeout: NodeJS.Timeout | null = null;

  @Mutation
  partialLogin(token: string): void {
    this.partiallyLogged = true;
    this.token = token;

    const decodedJWT: JwtInterface = jwtDecode(token);

    const actualDate = new Date(0);
    actualDate.setUTCSeconds(decodedJWT.exp);
    this.tokenExpiresIn = actualDate;

    const expirationDate = differenceInMilliseconds(actualDate, new Date());
    this.logoutWhenExpiredTimeout = setTimeout(() => {
      getModule(Auth).logout();
    }, expirationDate);
  }

  @Mutation
  public login(payload: CustomerProfileVm): void {
    CardApplication.initCardApplication();
    UserAccount.clearCardData();
    UserAccount.clearSelectedCustomerData();

    this.logged = true;
    this.partiallyLogged = false;
    this.token = payload.token;
    this.customerAccountId = payload.customerAccountId;
    this.username = payload.username;
    if (payload.customer) {
      CustomerModule.setCustomer(payload.customer);

      if (payload.customer.cartLifeTime) {
        ShoppingCartModule.setExpirationTime(payload.customer.cartLifeTime);
        ShoppingCartModule.setCartLifetimeTimer();
      }
    }

    const decodedJWT: JwtInterface = jwtDecode(payload.token);

    const actualDate = new Date(0);
    actualDate.setUTCSeconds(decodedJWT.exp);
    this.tokenExpiresIn = actualDate;

    const expirationDate = differenceInMilliseconds(actualDate, new Date());
    this.logoutWhenExpiredTimeout = setTimeout(() => {
      getModule(Auth).logout();
    }, expirationDate);
  }

  @Mutation
  partialLogout(): void {
    this.partiallyLogged = false;
    this.token = '';
    this.tokenExpiresIn = '';
    if (this.logoutWhenExpiredTimeout) {
      clearTimeout(this.logoutWhenExpiredTimeout);
    }
    router.push(Paths.LOGIN);
  }

  @Mutation
  public logout(): void {
    this.partiallyLogged = false;
    this.logged = false;
    this.token = '';
    this.customerAccountId = '';
    CustomerModule.clearLinkedCustomer();
    this.username = '';
    CustomerModule.clearCustomer();
    SystemConfigurationModule.clearSystemConfiguration();
    this.tokenExpiresIn = '';
    if (this.logoutWhenExpiredTimeout) {
      clearTimeout(this.logoutWhenExpiredTimeout);
    }

    CardApplication.initCardApplication();
    UserAccount.clearCardData();
    UserAccount.clearSelectedCustomerData();

    loadPublicSystemConfiguration();
    router.push(Paths.LOGIN);
  }

  public get loggedInAccountDisplayName() {
    return CustomerModule.hasLoggedInAccountCustomer ? formatName(CustomerModule.registeredCustomer) : this.username;
  }

  public get tokenExpired() {
    return differenceInMilliseconds(this.tokenExpiresIn, new Date()) <= 0;
  }
}

export default getModule(Auth);
